import { useEffect, useMemo, useState } from 'react'

import { useRouter } from 'next/router'

import { useAppSelector } from '~/redux/hooks'

import {
  selectVccHasWhyLevel,
  selectVccLevelActivities,
  selectVccPageLevelsData,
  selectVccPageTotalLevels,
} from '~/bootcamp/slices/vcc-data.slice'
import { PageLevel } from '~/types/bootcamp'

import { BootcampPageUrlParamsType, LevelActionType } from '../types/bootcamp.types'

interface ValueType {
  showPrevious: boolean
  showNext: boolean
  previousStepPath: string
  nextStepPath: string
  isNextShallowRoute: boolean
  isPreviousShallowRoute: boolean
}

const isFirstPage = (level: string, pageAction: string, hasWhyLevel: boolean, firstPage: PageLevel): boolean => {
  if (pageAction === LevelActionType.Why) return true
  if (!hasWhyLevel && level === '1') {
    if (
      (!firstPage.regular && pageAction === LevelActionType.Definition) ||
      (firstPage.regular && pageAction === LevelActionType.How)
    ) {
      return true
    }
  }

  return false
}

const useModuleNavigation = (): ValueType => {
  const [showPrevious, setPrevVisibility] = useState(true)
  const [showNext, setNextVisibility] = useState(true)
  const router = useRouter()
  const {
    action,
    key: bootcampKey,
    level,
    moduleKey,
    subtopic,
    type,
  } = router.query as unknown as BootcampPageUrlParamsType

  const totalLevels = useAppSelector(selectVccPageTotalLevels)
  const vccLevelsPageData = useAppSelector(selectVccPageLevelsData)
  const totalActivitiesByLevel = useAppSelector(selectVccLevelActivities)
  const hasWhyLevel: boolean = useAppSelector(selectVccHasWhyLevel) || false

  // To show/hide Previous and Next buttons
  useEffect(() => {
    // setPrevVisibility(action !== LevelActionType.Why)
    setPrevVisibility(!isFirstPage(level, action, hasWhyLevel, vccLevelsPageData[0]))
    setNextVisibility(action !== LevelActionType.Quiz)
  }, [level, totalLevels, action, hasWhyLevel, vccLevelsPageData])

  // All leaves/steps in a bootcamp
  const pageSteps = useMemo(() => {
    let step: Array<{
      level: string
      action: LevelActionType
      type?: number
    }> = []

    if (hasWhyLevel) {
      step = [...step, { level: '1', action: LevelActionType.Why }]
    }
    let hasEncyclopedia = false
    vccLevelsPageData?.forEach((page) => {
      if (page.regular) {
        step = [...step, { level: page.level, action: LevelActionType.How }]
        new Array(totalActivitiesByLevel[page.level]).fill(null).forEach((_, idx) => {
          step = [...step, { level: page.level, action: LevelActionType.Activity, type: idx + 1 }]
        })
      } else {
        if (page.hasEncyclopedia) {
          hasEncyclopedia = true
        }
        step = [...step, { level: page.level, action: LevelActionType.Definition }]
      }
    })

    // for encyclopedia article page we are removing the quiz page navigation
    if (!hasEncyclopedia) {
      step = [...step, { level: (totalLevels + 1).toString(), action: LevelActionType.Quiz }]
    }

    return step
  }, [hasWhyLevel, vccLevelsPageData, totalActivitiesByLevel, totalLevels])

  const currStepIndex = useMemo(() => {
    const currentActivityType = type === undefined ? type : parseInt(type)
    return pageSteps.findIndex(
      (page) => page.level === level && page.action === action && page.type === currentActivityType,
    )
  }, [action, level, pageSteps, type])

  const isNextShallowRoute = useMemo(() => {
    return action === LevelActionType.Activity && totalActivitiesByLevel[level] > parseInt(type ?? '')
  }, [action, level, totalActivitiesByLevel, type])

  const isPreviousShallowRoute = useMemo(() => {
    return action === LevelActionType.Activity && parseInt(type ?? '') > 1
  }, [action, type])

  const previousStepPath = useMemo(() => {
    const step = pageSteps[currStepIndex - 1] || {
      level: 1,
      action: hasWhyLevel ? LevelActionType.Why : LevelActionType.How,
    }
    return `/bootcamp/${subtopic}/${bootcampKey}/${moduleKey}/${step.level}/${step.action}${
      step.type ? `/${step.type}` : ''
    }`
  }, [pageSteps, currStepIndex, hasWhyLevel, subtopic, bootcampKey, moduleKey])

  const nextStepPath = useMemo(() => {
    const step = pageSteps[currStepIndex + 1]
    if (!step) {
      return ''
    }
    return `/bootcamp/${subtopic}/${bootcampKey}/${moduleKey}/${step.level}/${step.action}${
      step.type ? `/${step.type}` : ''
    }`
  }, [currStepIndex, pageSteps, bootcampKey, moduleKey, subtopic])

  return {
    showPrevious,
    showNext,
    previousStepPath,
    nextStepPath,
    isNextShallowRoute,
    isPreviousShallowRoute,
  }
}

export default useModuleNavigation
